import {
  ReconciliationBankAmounts,
  ReconciliationBankBalance,
  ReconciliationBankBreakdown,
  ReconciliationBankDay,
  ReconciliationBankHistory,
  ReconciliationBankMonth,
  ReconciliationBankMonthlyBreakdown,
  ReconciliationBankRow,
  ReconciliationCountAmount,
  ReconciliationCountryBreakdown,
  ReconciliationCountryHistory,
  ReconciliationCountryMonthlyBreakdown,
  ReconciliationCountryRow,
  ReconciliationDashboardSummary,
  ReconciliationHistory,
  ReconciliationMonthlySummary,
  ReconciliationPayment,
  ReconciliationPaymentOption,
  ReconciliationPaymentPage,
  ReconciliationProvider,
  ReconciliationPackageBreakdown,
  ReconciliationPackageHistory,
  ReconciliationPackageMonthlyBreakdown,
  ReconciliationPackageRow,
  ReconciliationValidationTotals,
  ReconciliationYearTotals,
  ReconciliationPendingDay,
  ReconciliationPendingMonth,
  ReconciliationPendingOperation,
  ReconciliationPendingRecognition,
  ReconciliationPendingTotals,
  ReconciliationPendingYear,
} from "../../../domain/models/accounting/reconciliation-dashboard.model";

type UnknownRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

const isText = (value: unknown): value is string => typeof value === "string" && value.trim().length > 0;

const optionalId = (value: unknown): number | null => (isFiniteNumber(value) ? value : null);

const optionalText = (value: unknown): string | null => (isText(value) ? value : null);

interface Breakdown<T> {
  currencyCode: string;
  periodKey: string;
  rows: T[];
  total: ReconciliationBankAmounts;
  updatedAt: string | null;
}

interface MonthlyBreakdown<T> {
  currencyCode: string;
  periodKey: string;
  rows: T[];
  total: ReconciliationValidationTotals;
  updatedAt: string | null;
}

const BANK_AMOUNT_FIELDS = [
  "paymentsCount",
  "subtotal",
  "commission",
  "systemTotal",
  "validatedByMacro",
  "difference",
] as const;

const VALIDATION_TOTAL_FIELDS = [
  "paymentsCount",
  "validatedCount",
  "accumulated",
  "validated",
  "difference",
  "validationPercentage",
] as const;

const MONTHLY_SUMMARY_FIELDS = [
  "paymentCount",
  "systemTotal",
  "macroTotal",
  "validatedTotal",
  "pendingTotal",
  "differenceTotal",
] as const;

const YEAR_MONTH = /^\d{4}-(0[1-9]|1[0-2])$/;

function toCountAmount(value: unknown): ReconciliationCountAmount | null {
  if (!isRecord(value) || !isFiniteNumber(value["count"]) || !isFiniteNumber(value["amount"])) {
    return null;
  }
  return { count: value["count"], amount: value["amount"] };
}

function toBankAmounts(value: unknown): ReconciliationBankAmounts | null {
  if (!isRecord(value) || !BANK_AMOUNT_FIELDS.every((field) => isFiniteNumber(value[field]))) {
    return null;
  }
  return {
    paymentsCount: value["paymentsCount"] as number,
    subtotal: value["subtotal"] as number,
    commission: value["commission"] as number,
    systemTotal: value["systemTotal"] as number,
    validatedByMacro: value["validatedByMacro"] as number,
    difference: value["difference"] as number,
    updatedAt: optionalText(value["updatedAt"]),
  };
}

function toBankRow(value: unknown): ReconciliationBankRow | null {
  const amounts = toBankAmounts(value);
  if (!amounts || !isRecord(value) || !isRecord(value["bank"]) || !isText(value["bank"]["code"])) {
    return null;
  }
  const code = value["bank"]["code"];
  const name = value["bank"]["name"];
  return { ...amounts, code, name: isText(name) ? name : code };
}

function toPackageRow(value: unknown): ReconciliationPackageRow | null {
  const amounts = toBankAmounts(value);
  if (!amounts || !isRecord(value) || !isRecord(value["family"]) || !isText(value["family"]["name"])) {
    return null;
  }
  return { ...amounts, id: optionalId(value["family"]["id"]), name: value["family"]["name"] };
}

function toCountryRow(value: unknown): ReconciliationCountryRow | null {
  const amounts = toBankAmounts(value);
  if (!amounts || !isRecord(value) || !isRecord(value["country"]) || !isText(value["country"]["name"])) {
    return null;
  }
  const country = value["country"];
  return {
    ...amounts,
    id: optionalId(country["id"]),
    iso: optionalText(country["iso"]),
    name: country["name"] as string,
    flagUrl: optionalText(country["icon"]),
  };
}

function toValidationTotals(value: unknown): ReconciliationValidationTotals | null {
  if (!isRecord(value) || !VALIDATION_TOTAL_FIELDS.every((field) => isFiniteNumber(value[field]))) {
    return null;
  }
  return {
    paymentsCount: value["paymentsCount"] as number,
    validatedCount: value["validatedCount"] as number,
    accumulated: value["accumulated"] as number,
    validated: value["validated"] as number,
    difference: value["difference"] as number,
    validationPercentage: value["validationPercentage"] as number,
    updatedAt: optionalText(value["updatedAt"]),
  };
}

function toBankDay(value: unknown): ReconciliationBankDay | null {
  if (!isRecord(value) || !isText(value["date"])) {
    return null;
  }
  const totals = toValidationTotals(value["totals"]);
  return totals ? { date: value["date"], totals } : null;
}

function toBankMonth(value: unknown): ReconciliationBankMonth | null {
  if (!isRecord(value) || !isFiniteNumber(value["year"]) || !isFiniteNumber(value["month"])) {
    return null;
  }
  const totals = toValidationTotals(value["totals"]);
  const days = Array.isArray(value["days"]) ? value["days"].map(toBankDay) : [];
  if (!totals || days.some((day) => day === null)) {
    return null;
  }
  return { year: value["year"], month: value["month"], totals, days: days as ReconciliationBankDay[] };
}

function toYearTotals(value: unknown): ReconciliationYearTotals | null {
  if (!isRecord(value) || !isFiniteNumber(value["year"])) {
    return null;
  }
  const totals = toValidationTotals(value["totals"]);
  return totals ? { year: value["year"], totals } : null;
}

function toHistory(value: UnknownRecord): ReconciliationHistory | null {
  const total = toValidationTotals(value["total"]);
  const years = Array.isArray(value["years"]) ? value["years"].map(toYearTotals) : [];
  const months = Array.isArray(value["months"]) ? value["months"].map(toBankMonth) : [];
  if (!total || years.some((year) => year === null) || months.some((month) => month === null)) {
    return null;
  }
  return { total, years: years as ReconciliationYearTotals[], months: months as ReconciliationBankMonth[] };
}

function toBankHistory(value: unknown): ReconciliationBankHistory | null {
  if (!isRecord(value) || !isRecord(value["bank"]) || !isText(value["bank"]["code"])) {
    return null;
  }
  const history = toHistory(value);
  if (!history) {
    return null;
  }
  const code = value["bank"]["code"];
  const name = value["bank"]["name"];
  return { ...history, code, name: isText(name) ? name : code };
}

function toPackageHistory(value: unknown): ReconciliationPackageHistory | null {
  if (!isRecord(value) || !isRecord(value["family"]) || !isText(value["family"]["name"])) {
    return null;
  }
  const history = toHistory(value);
  return history
    ? { ...history, id: optionalId(value["family"]["id"]), name: value["family"]["name"] }
    : null;
}

function toCountryHistory(value: unknown): ReconciliationCountryHistory | null {
  if (!isRecord(value) || !isRecord(value["country"]) || !isText(value["country"]["name"])) {
    return null;
  }
  const history = toHistory(value);
  const country = value["country"];
  return history
    ? {
        ...history,
        id: optionalId(country["id"]),
        iso: optionalText(country["iso"]),
        name: country["name"] as string,
        flagUrl: optionalText(country["icon"]),
      }
    : null;
}

function toMonthlyBreakdown<T>(
  value: unknown,
  rowsField: string,
  toRow: (row: unknown) => T | null,
): MonthlyBreakdown<T> | null {
  if (!isRecord(value) || !Array.isArray(value[rowsField])) {
    return null;
  }
  const rows = value[rowsField].map(toRow);
  const total = toValidationTotals(value["total"]);
  const currencyCode = value["currencyCode"];
  const periodKey = value["periodKey"];
  if (!total || !isText(currencyCode) || !isText(periodKey) || rows.some((row) => row === null)) {
    return null;
  }
  return { currencyCode, periodKey, rows: rows as T[], total, updatedAt: optionalText(value["updatedAt"]) };
}

function toBreakdown<T>(value: unknown, rowsField: string, toRow: (row: unknown) => T | null): Breakdown<T> | null {
  if (!isRecord(value) || !Array.isArray(value[rowsField])) {
    return null;
  }
  const rows = value[rowsField].map(toRow);
  const total = toBankAmounts(value["total"]);
  const currencyCode = value["currencyCode"];
  const periodKey = value["periodKey"];
  if (!total || !isText(currencyCode) || !isText(periodKey) || rows.some((row) => row === null)) {
    return null;
  }
  return { currencyCode, periodKey, rows: rows as T[], total, updatedAt: optionalText(value["updatedAt"]) };
}

export function toReconciliationDashboardSummary(value: unknown): ReconciliationDashboardSummary | null {
  if (!isRecord(value) || !isRecord(value["summary"])) {
    return null;
  }
  const systemTotal = toCountAmount(value["summary"]["systemTotal"]);
  const validatedByMacro = toCountAmount(value["summary"]["validatedByMacro"]);
  const currencyCode = value["currencyCode"];
  const periodKey = value["periodKey"];
  if (!systemTotal || !validatedByMacro || !isText(currencyCode) || !isText(periodKey)) {
    return null;
  }
  return {
    currencyCode,
    periodKey,
    systemTotal,
    validatedByMacro,
    updatedAt: isText(value["updatedAt"]) ? value["updatedAt"] : null,
  };
}

export function toReconciliationBankBreakdown(value: unknown): ReconciliationBankBreakdown | null {
  const breakdown = toBreakdown(value, "banks", toBankRow);
  if (!breakdown) {
    return null;
  }
  const { rows, ...header } = breakdown;
  return { ...header, banks: rows };
}

export function toReconciliationPackageBreakdown(value: unknown): ReconciliationPackageBreakdown | null {
  const breakdown = toBreakdown(value, "families", toPackageRow);
  if (!breakdown) {
    return null;
  }
  const { rows, ...header } = breakdown;
  return { ...header, packages: rows };
}

export function toReconciliationCountryBreakdown(value: unknown): ReconciliationCountryBreakdown | null {
  const breakdown = toBreakdown(value, "countries", toCountryRow);
  if (!breakdown) {
    return null;
  }
  const { rows, ...header } = breakdown;
  return { ...header, countries: rows };
}

export function toReconciliationBankMonthlyBreakdown(value: unknown): ReconciliationBankMonthlyBreakdown | null {
  const breakdown = toMonthlyBreakdown(value, "banks", toBankHistory);
  if (!breakdown) {
    return null;
  }
  const { rows, ...header } = breakdown;
  return { ...header, banks: rows };
}

export function toReconciliationPackageMonthlyBreakdown(
  value: unknown,
): ReconciliationPackageMonthlyBreakdown | null {
  const breakdown = toMonthlyBreakdown(value, "families", toPackageHistory);
  if (!breakdown) {
    return null;
  }
  const { rows, ...header } = breakdown;
  return { ...header, packages: rows };
}

export function toReconciliationCountryMonthlyBreakdown(
  value: unknown,
): ReconciliationCountryMonthlyBreakdown | null {
  const breakdown = toMonthlyBreakdown(value, "countries", toCountryHistory);
  if (!breakdown) {
    return null;
  }
  const { rows, ...header } = breakdown;
  return { ...header, countries: rows };
}

function toMonthlySummary(value: unknown): ReconciliationMonthlySummary | null {
  if (
    !isRecord(value)
    || !isText(value["yearMonth"])
    || !YEAR_MONTH.test(value["yearMonth"])
    || !MONTHLY_SUMMARY_FIELDS.every((field) => isFiniteNumber(value[field]))
  ) {
    return null;
  }
  return {
    yearMonth: value["yearMonth"],
    paymentCount: value["paymentCount"] as number,
    systemTotal: value["systemTotal"] as number,
    macroTotal: value["macroTotal"] as number,
    validatedTotal: value["validatedTotal"] as number,
    pendingTotal: value["pendingTotal"] as number,
    differenceTotal: value["differenceTotal"] as number,
    updatedAt: optionalText(value["updatedAt"]),
  };
}

export function toReconciliationMonthlySummaries(value: unknown): ReconciliationMonthlySummary[] | null {
  if (!Array.isArray(value)) {
    return null;
  }
  const months = value.map(toMonthlySummary);
  return months.some((month) => month === null) ? null : (months as ReconciliationMonthlySummary[]);
}

export function toReconciliationBankBalance(value: unknown): ReconciliationBankBalance | null {
  if (!isRecord(value) || !isFiniteNumber(value["idPeriod"]) || !isFiniteNumber(value["balance"]) || !isText(value["capturedAt"])) {
    return null;
  }
  return { idPeriod: value["idPeriod"], balance: value["balance"], capturedAt: value["capturedAt"] };
}

function toPayment(value: unknown): ReconciliationPayment | null {
  if (!isRecord(value) || !isFiniteNumber(value["id"]) || !isText(value["status"]) || !isFiniteNumber(value["amount"])) {
    return null;
  }
  return {
    id: value["id"],
    status: value["status"],
    operationNumber: optionalText(value["operationNumber"]),
    macroOperationNumber: optionalText(value["macroOperationNumber"]),
    macroCode: optionalText(value["macroCode"]),
    macroFileName: optionalText(value["macroFileName"]),
    macroFileUrl: optionalText(value["macroFileUrl"]),
    voucherId: optionalId(value["voucherId"]),
    voucherUrl: optionalText(value["voucherUrl"]),
    userId: optionalId(value["userId"]),
    username: optionalText(value["username"]),
    fullName: optionalText(value["fullName"]),
    email: optionalText(value["email"]),
    documentNumber: optionalText(value["documentNumber"]),
    phone: optionalText(value["phone"]),
    userStateId: optionalId(value["userStateId"]),
    countryCode: optionalText(value["countryCode"]),
    paymentDate: optionalText(value["paymentDate"]),
    bankName: optionalText(value["bankName"]),
    paymentType: optionalText(value["paymentType"]),
    packageName: optionalText(value["packageName"]),
    familyName: optionalText(value["familyName"]),
    installment: optionalText(value["installment"]),
    paidAt: optionalText(value["paidAt"]),
    commission: optionalId(value["commission"]),
    amount: value["amount"],
    processTypeId: optionalId(value["processTypeId"]),
    paymentTypeId: optionalId(value["paymentTypeId"]),
    paymentSubtypeId: optionalId(value["paymentSubtypeId"]),
    paymentId: optionalId(value["paymentId"]),
    initialQuoteFlag: optionalId(value["initialQuoteFlag"]),
    quoteUsd: optionalId(value["quoteUsd"]),
    subtotal: optionalId(value["subtotal"]),
    subscriptionId: optionalId(value["subscriptionId"]),
    subscriptionCreatedAt: optionalText(value["subscriptionCreatedAt"]),
    subscriptionStatusId: optionalId(value["subscriptionStatusId"]),
    providerCode: optionalText(value["providerCode"]),
  };
}

export function toReconciliationPaymentPage(value: unknown): ReconciliationPaymentPage | null {
  if (
    !isRecord(value)
    || !Array.isArray(value["payments"])
    || !isFiniteNumber(value["page"])
    || !isFiniteNumber(value["size"])
    || !isFiniteNumber(value["totalElements"])
  ) {
    return null;
  }
  const payments = value["payments"].map(toPayment);
  if (payments.some((payment) => payment === null)) {
    return null;
  }
  return {
    payments: payments as ReconciliationPayment[],
    page: value["page"],
    size: value["size"],
    totalElements: value["totalElements"],
    totalAmount: optionalId(value["totalAmount"]),
  };
}

function toPaymentOption(value: unknown): ReconciliationPaymentOption | null {
  if (!isRecord(value)) {
    return null;
  }
  return {
    paymentTypeId: optionalId(value["paymentTypeId"]),
    paymentTypeName: optionalText(value["paymentTypeName"]),
    paymentSubtypeId: optionalId(value["paymentSubtypeId"]),
    paymentSubtypeName: optionalText(value["paymentSubtypeName"]),
  };
}

export function toReconciliationPaymentOptions(value: unknown): ReconciliationPaymentOption[] | null {
  if (!Array.isArray(value)) {
    return null;
  }
  const options = value.map(toPaymentOption);
  return options.some((option) => option === null) ? null : (options as ReconciliationPaymentOption[]);
}

function toProvider(value: unknown): ReconciliationProvider | null {
  if (!isRecord(value) || !isText(value["code"]) || !isText(value["name"])) {
    return null;
  }
  return { code: value["code"], name: value["name"] };
}

export function toReconciliationProviders(value: unknown): ReconciliationProvider[] | null {
  if (!Array.isArray(value)) {
    return null;
  }
  const providers = value.map(toProvider);
  return providers.some((provider) => provider === null) ? null : (providers as ReconciliationProvider[]);
}

const countOf = (value: unknown): number => (isFiniteNumber(value) ? value : 0);

const listOf = <T>(value: unknown, parse: (item: unknown) => T | null): T[] | null => {
  if (value === undefined || value === null) {
    return [];
  }
  if (!Array.isArray(value)) {
    return null;
  }
  const items = value.map(parse);
  return items.some((item) => item === null) ? null : (items as T[]);
};

function toPendingTotals(value: unknown): ReconciliationPendingTotals | null {
  if (
    !isRecord(value)
    || !isFiniteNumber(value["accumulated"])
    || !isFiniteNumber(value["validated"])
    || !isFiniteNumber(value["pending"])
  ) {
    return null;
  }
  return {
    operationsCount: countOf(value["operationsCount"]),
    validatedCount: countOf(value["validatedCount"]),
    pendingCount: countOf(value["pendingCount"]),
    accumulated: value["accumulated"],
    validated: value["validated"],
    pending: value["pending"],
    validationPercentage: countOf(value["validationPercentage"]),
  };
}

function toPendingYear(value: unknown): ReconciliationPendingYear | null {
  if (!isRecord(value) || !isFiniteNumber(value["year"])) {
    return null;
  }
  const totals = toPendingTotals(value["totals"]);
  return totals ? { year: value["year"], totals, updatedAt: optionalText(value["updatedAt"]) } : null;
}

function toPendingMonth(value: unknown): ReconciliationPendingMonth | null {
  if (!isRecord(value) || !isFiniteNumber(value["year"]) || !isFiniteNumber(value["month"])) {
    return null;
  }
  const totals = toPendingTotals(value["totals"]);
  return totals
    ? { year: value["year"], month: value["month"], totals, updatedAt: optionalText(value["updatedAt"]) }
    : null;
}

function toPendingOperation(value: unknown): ReconciliationPendingOperation | null {
  if (!isRecord(value) || !isFiniteNumber(value["amount"])) {
    return null;
  }
  const bank = isRecord(value["bank"]) ? value["bank"] : {};
  return {
    macroOperationId: optionalId(value["macroOperationId"]),
    macroCode: optionalText(value["macroCode"]),
    bankCode: optionalText(bank["code"]),
    bankName: optionalText(bank["name"]),
    operationNumber: optionalText(value["operationNumber"]),
    operationDate: optionalText(value["operationDate"]),
    amount: value["amount"],
    description: optionalText(value["description"]),
    senderName: optionalText(value["senderName"]),
    status: optionalText(value["status"]),
    loadedAt: optionalText(value["loadedAt"]),
  };
}

function toPendingDay(value: unknown): ReconciliationPendingDay | null {
  if (!isRecord(value) || !isText(value["date"])) {
    return null;
  }
  const operations = listOf(value["operations"], toPendingOperation);
  return operations
    ? {
      date: value["date"],
      totals: toPendingTotals(value["totals"]),
      updatedAt: optionalText(value["updatedAt"]),
      operations,
    }
    : null;
}

export function toReconciliationPendingRecognition(value: unknown): ReconciliationPendingRecognition | null {
  if (!isRecord(value) || !isText(value["currencyCode"]) || !isText(value["periodKey"])) {
    return null;
  }
  const total = toPendingTotals(value["total"]);
  const years = listOf(value["years"], toPendingYear);
  const months = listOf(value["months"], toPendingMonth);
  const days = listOf(value["days"], toPendingDay);
  if (!total || !years || !months || !days) {
    return null;
  }
  return {
    currencyCode: value["currencyCode"],
    periodKey: value["periodKey"],
    updatedAt: optionalText(value["updatedAt"]),
    total,
    years,
    months,
    days,
  };
}
